import React, { useState } from 'react';
import {
  ArrowLeft,
  Search,
  ChevronDown,
  FileText,
  Image as ImageIcon,
  Download,
  Printer,
  X,
  Plus,
  Eye,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { ResidentSubMenuKey } from '../ResidentDirectory';

interface Props {
  onBack: () => void;
  onNavigate?: (tab: ResidentSubMenuKey) => void;
}

interface PetRecord {
  id: string;
  petType: string; // e.g. "น้องแมว", "แมวเหมียว", "ปลากัด น้องถุงเงิน"
  characteristics: string; // e.g. "99/99 ชื่อเฌอแตม ตัวสีดำล้วน...", "อ้วนกลม"
  imageUrl: string;
  imageAlt: string;
  healthCert?: {
    type: 'pdf' | 'jpg';
    fileName: string;
    issueDate?: string;
    clinicName?: string;
  };
}

interface RoomPetGroup {
  roomNumber: string;
  pets: PetRecord[];
}

const INITIAL_PET_DATA: RoomPetGroup[] = [
  {
    roomNumber: '1086/01',
    pets: [
      {
        id: 'p-1',
        petType: 'น้องแมว',
        characteristics:
          '99/99 ชื่อเฌอแตม ตัวสีดำล้วน ท้องมีสีขาว และปลายเท้ามีสีขาว หางยาว มีปลอกคอ มีชื่อ และเบอร์โทร061-679-4644',
        imageUrl:
          'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'น้องแมว เฌอแตม',
      },
      {
        id: 'p-2',
        petType: 'แมวเหมียว',
        characteristics: 'อ้วนกลม',
        imageUrl:
          'https://images.unsplash.com/photo-1573865526739-10659fec78a5?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'แมวเหมียว',
        healthCert: {
          type: 'pdf',
          fileName: '20297-7749820230307214623.pdf',
          issueDate: '07/03/2026',
          clinicName: 'โรงพยาบาลสัตว์กรุงเทพ',
        },
      },
      {
        id: 'p-3',
        petType: 'หนูน่า',
        characteristics: 'หูใหญ่',
        imageUrl:
          'https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'หนูน่า',
      },
      {
        id: 'p-4',
        petType: 'ปลากัด น้องถุงเงิน',
        characteristics: 'สีน้ำเงิน',
        imageUrl:
          'https://images.unsplash.com/photo-1522069169874-c58ec4b76be5?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'ปลากัด น้องถุงเงิน',
      },
      {
        id: 'p-5',
        petType: 'ไก่ชนบุรี',
        characteristics: 'สีดำ-แดง',
        imageUrl:
          'https://images.unsplash.com/photo-1548550023-2bdb3c5beed7?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'ไก่ชนบุรี',
      },
      {
        id: 'p-6',
        petType: 'นานาจ่า',
        characteristics: 'ดื้อ',
        imageUrl:
          'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'นานาจ่า นกแก้ว',
      },
      {
        id: 'p-7',
        petType: 'นกแก้ว',
        characteristics: 'สีเขียว',
        imageUrl:
          'https://images.unsplash.com/photo-1452570053594-1b985d6ea890?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'นกแก้ว',
      },
      {
        id: 'p-8',
        petType: 'ไก่',
        characteristics: 'พูดมากๆ',
        imageUrl:
          'https://images.unsplash.com/photo-1563281577-a7be47e20db9?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'ไก่',
      },
      {
        id: 'p-9',
        petType: 'แมว',
        characteristics: 'สีขาว-ส้ม',
        imageUrl:
          'https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'แมว สีขาว-ส้ม',
        healthCert: {
          type: 'jpg',
          fileName: '20297-4104720230308071031.jpeg',
          issueDate: '08/03/2026',
          clinicName: 'คลินิกรักษาสัตว์บางปะอิน',
        },
      },
    ],
  },
  {
    roomNumber: '1086/02',
    pets: [
      {
        id: 'p-10',
        petType: 'แมวแมว',
        characteristics: 'อ้วนมาก',
        imageUrl:
          'https://images.unsplash.com/photo-1518791841217-8f162f1e1131?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'แมวแมว',
        healthCert: {
          type: 'jpg',
          fileName: '20297-1594820230307214817.jpeg',
          issueDate: '07/03/2026',
          clinicName: 'โรงพยาบาลสัตว์ทองหล่อ',
        },
      },
      {
        id: 'p-11',
        petType: 'หมี',
        characteristics: 'สีดำ-ดำมาก',
        imageUrl:
          'https://images.unsplash.com/photo-1537151608828-ea2b11777ee8?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'หมี',
      },
      {
        id: 'p-12',
        petType: 'สิงโต',
        characteristics: 'สีน้ำตาล-ดำ',
        imageUrl:
          'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'สิงโต',
      },
    ],
  },
  {
    roomNumber: '1086/03',
    pets: [
      {
        id: 'p-13',
        petType: 'แมว',
        characteristics: 'เท้าขาว 4 ข้าง',
        imageUrl:
          'https://images.unsplash.com/photo-1543852786-1cf6624b9987?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'แมว เท้าขาว 4 ข้าง',
      },
    ],
  },
  {
    roomNumber: '1086/04',
    pets: [
      {
        id: 'p-14',
        petType: 'adf',
        characteristics: 'asdff',
        imageUrl:
          'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=200&auto=format&fit=crop&q=80',
        imageAlt: 'adf',
      },
    ],
  },
];

export const ResidentPetsView: React.FC<Props> = ({ onBack, onNavigate }) => {
  const [data, setData] = useState<RoomPetGroup[]>(INITIAL_PET_DATA);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; title: string } | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<{
    fileName: string;
    type: 'pdf' | 'jpg';
    room: string;
    petType: string;
    clinicName?: string;
  } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // New Pet Form
  const [formRoom, setFormRoom] = useState('1086/01');
  const [formPetType, setFormPetType] = useState('');
  const [formCharacteristics, setFormCharacteristics] = useState('');
  const [formCertFile, setFormCertFile] = useState('');

  // Search filter
  const filteredData = data
    .map((group) => {
      const q = appliedSearch.toLowerCase().trim();
      if (!q) return group;

      const roomMatch = group.roomNumber.toLowerCase().includes(q);
      const filteredPets = group.pets.filter(
        (p) =>
          p.petType.toLowerCase().includes(q) ||
          p.characteristics.toLowerCase().includes(q) ||
          (p.healthCert && p.healthCert.fileName.toLowerCase().includes(q))
      );

      if (roomMatch) return group;
      if (filteredPets.length > 0) {
        return {
          ...group,
          pets: filteredPets,
        };
      }
      return null;
    })
    .filter(Boolean) as RoomPetGroup[];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchQuery);
  };

  const handleExportPDF = () => {
    setShowPrintDropdown(false);
    window.print();
  };

  const handleExportExcel = () => {
    setShowPrintDropdown(false);
    // Generate CSV data for download
    let csv = 'ห้อง,ประเภทสัตว์เลี้ยง,ลักษณะ,ใบตรวจสุขภาพประจำปี\n';
    data.forEach((group) => {
      group.pets.forEach((pet) => {
        const cert = pet.healthCert ? pet.healthCert.fileName : '-';
        csv += `"${group.roomNumber}","${pet.petType}","${pet.characteristics.replace(/"/g, '""')}","${cert}"\n`;
      });
    });

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `รายงานข้อมูลสัตว์เลี้ยง_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleAddPet = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formPetType.trim()) return;

    const newPet: PetRecord = {
      id: `p-${Date.now()}`,
      petType: formPetType.trim(),
      characteristics: formCharacteristics.trim() || 'สมบูรณ์ แข็งแรง',
      imageUrl:
        'https://images.unsplash.com/photo-1548767797-d8c844163c4c?w=200&auto=format&fit=crop&q=80',
      imageAlt: formPetType.trim(),
      healthCert: formCertFile
        ? {
            type: formCertFile.endsWith('.pdf') ? 'pdf' : 'jpg',
            fileName: formCertFile.trim(),
            issueDate: new Date().toLocaleDateString('th-TH'),
          }
        : undefined,
    };

    const existingGroup = data.find((g) => g.roomNumber === formRoom);
    if (existingGroup) {
      setData(
        data.map((g) =>
          g.roomNumber === formRoom ? { ...g, pets: [...g.pets, newPet] } : g
        )
      );
    } else {
      setData([...data, { roomNumber: formRoom, pets: [newPet] }]);
    }

    setShowAddModal(false);
    setFormPetType('');
    setFormCharacteristics('');
    setFormCertFile('');
  };

  const tabs: { key: ResidentSubMenuKey; label: string }[] = [
    { key: 'members', label: 'เจ้าของร่วม/สมาชิก' },
    { key: 'vehicles', label: 'ยานพาหนะ' },
    { key: 'keycards', label: 'คีย์การ์ด' },
    { key: 'foreign_ownership', label: 'กรรมสิทธิ์ชาวต่างชาติ' },
    { key: 'pets', label: 'สัตว์เลี้ยง' },
    { key: 'title_deeds', label: 'กรรมสิทธิ์' },
    { key: 'history_logs', label: 'ประวัติผู้อยู่อาศัย' },
  ];

  return (
    <div className="font-prompt bg-white min-h-[85vh] rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
      {/* 1. Top Sub-Navigation Tabs matching image */}
      <div className="border-b border-slate-200 bg-slate-50/50 px-4 sm:px-6 pt-3 overflow-x-auto">
        <div className="flex items-center gap-6 sm:gap-8 min-w-max text-xs sm:text-sm">
          {tabs.map((tab) => {
            const isActive = tab.key === 'pets';
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (tab.key === 'pets') return;
                  if (onNavigate) {
                    onNavigate(tab.key);
                  } else {
                    onBack();
                  }
                }}
                className={`pb-3 font-medium transition-colors cursor-pointer relative ${
                  isActive
                    ? 'text-slate-900 font-bold border-b-2 border-teal-600'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-5">
        {/* 2. Header Bar: Back Button + Title + Print Dropdown + Search Input */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Left: Back Button & Page Title */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors shadow-2xs cursor-pointer"
              title="ย้อนกลับไปเมนูหลัก"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
              ข้อมูลสัตว์เลี้ยง
            </h1>
          </div>

          {/* Right: Print Button & Search Form */}
          <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap justify-end">
            {/* Quick Add Button */}
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="px-3 py-1.5 bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" /> เพิ่มสัตว์เลี้ยง
            </button>

            {/* Print Dropdown Button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPrintDropdown(!showPrintDropdown)}
                className="px-3.5 py-1.5 bg-white border border-[#3b82f6] text-[#2563eb] hover:bg-blue-50/60 rounded-md text-xs font-medium flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
              >
                <span>พิมพ์</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {/* Dropdown Menu matching image */}
              {showPrintDropdown && (
                <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-30 text-xs animate-in fade-in zoom-in-95">
                  <button
                    type="button"
                    onClick={handleExportPDF}
                    className="w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
                  >
                    <FileText className="w-3.5 h-3.5 text-rose-500" />
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={handleExportExcel}
                    className="w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5 text-emerald-600" />
                    Excel
                  </button>
                </div>
              )}
            </div>

            {/* Search Box with Search Button attached */}
            <form onSubmit={handleSearch} className="flex items-center">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="ค้นหา"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded-l-md text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 w-36 sm:w-48"
                />
              </div>
              <button
                type="submit"
                className="px-3.5 py-1.5 bg-[#0066cc] hover:bg-[#0052a3] text-white text-xs font-semibold rounded-r-md border border-[#0066cc] shadow-2xs transition-colors cursor-pointer"
              >
                ค้นหา
              </button>
            </form>
          </div>
        </div>

        {/* 3. Main Pet Table matching exact column names & layout from image */}
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              {/* Header Row */}
              <thead>
                <tr className="bg-[#eef2f6] text-slate-700 font-semibold border-b border-slate-200 text-xs">
                  <th className="py-3 px-4 w-28 text-slate-700 font-medium">ห้อง</th>
                  <th className="py-3 px-4 w-44 text-slate-700 font-medium">ประเภทสัตว์เลี้ยง</th>
                  <th className="py-3 px-4 text-slate-700 font-medium">ลักษณะ</th>
                  <th className="py-3 px-4 w-24 text-center text-slate-700 font-medium">รูปภาพ</th>
                  <th className="py-3 px-4 w-72 text-slate-700 font-medium">ใบตรวจสุขภาพประจำปี</th>
                </tr>
              </thead>

              {/* Body */}
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400">
                      ไม่พบข้อมูลสัตว์เลี้ยงที่ตรงกับเงื่อนไขการค้นหา
                    </td>
                  </tr>
                ) : (
                  filteredData.map((group, groupIdx) => {
                    return group.pets.map((pet, petIdx) => {
                      const isFirstInGroup = petIdx === 0;
                      return (
                        <tr
                          key={pet.id}
                          className={`hover:bg-slate-50/80 transition-colors ${
                            isFirstInGroup && groupIdx !== 0 ? 'border-t-2 border-slate-200' : ''
                          }`}
                        >
                          {/* Room Number Column (Rowspan effect or first item display) */}
                          <td className="py-3 px-4 align-top">
                            {isFirstInGroup ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setSearchQuery(group.roomNumber);
                                  setAppliedSearch(group.roomNumber);
                                }}
                                className="font-semibold text-[#0066cc] hover:underline font-mono text-xs cursor-pointer block"
                                title={`คลิกเพื่อกรองห้อง ${group.roomNumber}`}
                              >
                                {group.roomNumber}
                              </button>
                            ) : null}
                          </td>

                          {/* Pet Type */}
                          <td className="py-3 px-4 align-middle font-medium text-slate-800">
                            {pet.petType}
                          </td>

                          {/* Characteristics */}
                          <td className="py-3 px-4 align-middle text-slate-700 leading-relaxed max-w-md">
                            {pet.characteristics}
                          </td>

                          {/* Pet Image Thumbnail */}
                          <td className="py-2.5 px-4 align-middle text-center">
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedImage({
                                  url: pet.imageUrl,
                                  title: `${pet.petType} (${group.roomNumber})`,
                                })
                              }
                              className="group relative inline-block rounded-md overflow-hidden border border-slate-200 hover:border-blue-400 hover:shadow-xs transition-all cursor-pointer"
                              title="คลิกเพื่อดูรูปภาพขนาดเต็ม"
                            >
                              <img
                                src={pet.imageUrl}
                                alt={pet.imageAlt}
                                className="w-10 h-10 object-cover group-hover:scale-110 transition-transform duration-200"
                                referrerPolicy="no-referrer"
                              />
                              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <Eye className="w-3.5 h-3.5 text-white drop-shadow-xs" />
                              </div>
                            </button>
                          </td>

                          {/* Annual Health Checkup Document */}
                          <td className="py-3 px-4 align-middle">
                            {pet.healthCert ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedDoc({
                                    fileName: pet.healthCert!.fileName,
                                    type: pet.healthCert!.type,
                                    room: group.roomNumber,
                                    petType: pet.petType,
                                    clinicName: pet.healthCert!.clinicName,
                                  })
                                }
                                className="inline-flex items-center gap-1.5 text-[11px] text-slate-700 hover:text-blue-700 hover:underline transition-colors text-left group cursor-pointer"
                                title="คลิกเพื่อเปิดดูเอกสาร"
                              >
                                {pet.healthCert.type === 'pdf' ? (
                                  <div className="flex items-center gap-1 shrink-0">
                                    {/* Red PDF Icon badge matching screenshot */}
                                    <div className="w-4 h-5 bg-rose-50 border border-rose-200 rounded-[3px] flex flex-col items-center justify-center p-0.5">
                                      <span className="text-[7px] font-bold text-rose-600 uppercase leading-none">
                                        PDF
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 shrink-0">
                                    {/* Green/Teal JPG Icon badge matching screenshot */}
                                    <div className="w-4 h-5 bg-teal-50 border border-teal-200 rounded-[3px] flex flex-col items-center justify-center p-0.5">
                                      <span className="text-[7px] font-bold text-teal-600 uppercase leading-none">
                                        JPG
                                      </span>
                                    </div>
                                  </div>
                                )}
                                <span className="font-mono text-slate-600 group-hover:text-blue-600 truncate max-w-xs sm:max-w-md">
                                  {pet.healthCert.fileName}
                                </span>
                              </button>
                            ) : (
                              <span className="text-slate-400 font-mono">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Summary / Count */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
          <span>
            แสดงสัตว์เลี้ยงทั้งหมด{' '}
            <strong className="text-slate-800">
              {filteredData.reduce((sum, g) => sum + g.pets.length, 0)}
            </strong>{' '}
            รายการ ใน{' '}
            <strong className="text-slate-800">{filteredData.length}</strong> ห้อง
          </span>
          <span className="text-slate-400">โครงการ ออร์คิด พาร์ค วิลล์</span>
        </div>
      </div>

      {/* Modal: Full Image Viewer */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
              <span className="font-semibold text-xs truncate">{selectedImage.title}</span>
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="p-1 text-slate-300 hover:text-white rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-slate-950">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="max-h-[60vh] max-w-full rounded-lg object-contain"
                referrerPolicy="no-referrer"
              />
            </div>
            <div className="p-3 bg-white text-center border-t border-slate-100">
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Health Certificate Document Viewer */}
      {selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="p-4 bg-teal-800 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-200" />
                <div>
                  <h3 className="font-bold text-xs sm:text-sm">ใบรับรองการตรวจสุขภาพสัตว์เลี้ยง</h3>
                  <span className="text-[10px] text-teal-200">
                    ห้อง {selectedDoc.room} • {selectedDoc.petType}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDoc(null)}
                className="p-1 text-teal-200 hover:text-white rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">ชื่อไฟล์เอกสาร:</span>
                  <span className="font-mono font-semibold text-slate-800 truncate max-w-xs">
                    {selectedDoc.fileName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">ประเภทไฟล์:</span>
                  <span className="font-semibold uppercase text-teal-700 font-mono">
                    {selectedDoc.type} DOCUMENT
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">สถานพยาบาลสัตว์:</span>
                  <span className="font-medium text-slate-800">
                    {selectedDoc.clinicName || 'โรงพยาบาลสัตว์ที่ได้รับการรับรอง'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">สถานะการตรวจสอบ:</span>
                  <span className="text-emerald-700 font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> ตรวจสอบผ่านเกณฑ์นิติฯ
                  </span>
                </div>
              </div>

              {/* Mock Certificate Preview Canvas */}
              <div className="p-5 border-2 border-dashed border-slate-300 rounded-xl bg-white text-center space-y-2">
                <div className="w-12 h-12 mx-auto rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700">
                  <FileText className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-slate-800">CERTIFICATE OF VETERINARY INSPECTION</h4>
                <p className="text-[11px] text-slate-500 max-w-xs mx-auto">
                  ได้รับการฉีดวัคซีนป้องกันโรคพิษสุนัขบ้า (Rabies) และตรวจสุขภาพประจำปีเรียบร้อยแล้ว
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setSelectedDoc(null)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 cursor-pointer"
                >
                  ปิด
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const blob = new Blob(
                      [
                        `ใบตรวจสุขภาพสัตว์เลี้ยง\nห้อง: ${selectedDoc.room}\nสัตว์เลี้ยง: ${selectedDoc.petType}\nเอกสาร: ${selectedDoc.fileName}`,
                      ],
                      { type: 'text/plain;charset=utf-8' }
                    );
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = selectedDoc.fileName;
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-4 py-2 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-bold flex items-center gap-1.5 shadow-xs cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" /> ดาวน์โหลดเอกสาร
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add New Pet */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in-95">
            <div className="bg-teal-800 p-4 text-white flex items-center justify-between">
              <h3 className="font-bold text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> เพิ่มข้อมูลสัตว์เลี้ยงใหม่
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="p-1 text-slate-300 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddPet} className="p-5 space-y-3.5 text-xs">
              <div>
                <label className="font-semibold text-slate-700 block mb-1">หมายเลขห้อง *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น 1086/01, 1086/02"
                  value={formRoom}
                  onChange={(e) => setFormRoom(e.target.value)}
                  className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">
                  ประเภทสัตว์เลี้ยง / ชื่อเรียก *
                </label>
                <input
                  type="text"
                  required
                  placeholder="เช่น น้องแมว, สุนัขโกลเด้น, นกแก้ว"
                  value={formPetType}
                  onChange={(e) => setFormPetType(e.target.value)}
                  className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">ลักษณะเด่น / รายละเอียด</label>
                <textarea
                  rows={2}
                  placeholder="เช่น สีดำ-ขาว อ้วนกลม สวมปลอกคอสีแดง"
                  value={formCharacteristics}
                  onChange={(e) => setFormCharacteristics(e.target.value)}
                  className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">
                  ชื่อไฟล์ใบตรวจสุขภาพ (PDF / JPEG)
                </label>
                <input
                  type="text"
                  placeholder="เช่น 20297-7749820230307214623.pdf"
                  value={formCertFile}
                  onChange={(e) => setFormCertFile(e.target.value)}
                  className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-bold shadow-xs"
                >
                  บันทึกข้อมูล
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
